/**
 * Divider Component
 * Visual separator with color, size, orientation, and variant options
 */

import React from "react";
import { Divider as ChakraDivider } from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { BorderColorToken, Color } from "design-system/foundation/Color";
import { CommonStyleProps, extractCommonProps } from "design-system/types/CommonProps";
import { resolveSpacing, resolveSize, resolveRadius } from "design-system/foundation";

// Branded types for Divider
declare const __divider_size_brand: unique symbol;
declare const __divider_variant_brand: unique symbol;
declare const __divider_orientation_brand: unique symbol;

export interface DividerSizeToken {
  readonly [__divider_size_brand]: true;
  readonly _value: string;
}

export interface DividerVariantToken {
  readonly [__divider_variant_brand]: true;
  readonly _value: string;
}

export interface DividerOrientationToken {
  readonly [__divider_orientation_brand]: true;
  readonly _value: string;
}

// Factory functions
const createDividerSizeToken = (value: string): DividerSizeToken => ({ _value: value } as DividerSizeToken);

const createDividerVariantToken = (value: string): DividerVariantToken => ({ _value: value } as DividerVariantToken);

const createDividerOrientationToken = (value: string): DividerOrientationToken => ({ _value: value } as DividerOrientationToken);

export interface DividerProps extends CommonStyleProps {
  color?: BorderColorToken;
  size?: DividerSizeToken;
  variant?: DividerVariantToken;
  orientation?: DividerOrientationToken;
  // Common React props
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  // Accessibility
  "aria-label"?: string;
  role?: string;
}

function DividerComponent(allProps: DividerProps) {
  const {
    color = Color.border.secondary,
    size = dividerTokens.size.thin,
    variant = dividerTokens.variant.solid,
    orientation = dividerTokens.orientation.horizontal,
    ...restProps
  } = allProps;

  // Extract and resolve common styling props
  const { cdsProps, remainingProps } = extractCommonProps(restProps);

  // Resolve CDS tokens to CSS values
  const resolvedColor = useColorModeValue(color);

  // Resolve common props
  const resolvedCommonProps: Record<string, string> = {};
  Object.entries(cdsProps).forEach(([key, value]) => {
    if (value && typeof value === "object" && "_spacingToken" in value) {
      // It's a radius token
      resolvedCommonProps[key] = resolveRadius(value as any);
    } else if (value && typeof value === "object" && "_value" in value) {
      // It's a spacing or length token
      if (key.includes("p") || key.includes("m") || key === "borderWidth") {
        resolvedCommonProps[key] = resolveSpacing(value as any);
      } else {
        resolvedCommonProps[key] = resolveSize(value as any);
      }
    } else if (value && typeof value === "object" && ("_semantic" in value || "_hue" in value)) {
      // It's a color token
      resolvedCommonProps[key] = useColorModeValue(value as any);
    }
  });

  const getSizeStyles = (size: DividerSizeToken) => {
    switch (size._value) {
      case "thick":
        return { borderWidth: "2px" };
      case "medium":
        return { borderWidth: "1.5px" };
      case "thin":
      default:
        return { borderWidth: "1px" };
    }
  };

  const getVariantStyles = (variant: DividerVariantToken) => {
    switch (variant._value) {
      case "dashed":
        return { borderStyle: "dashed" };
      case "dotted":
        return { borderStyle: "dotted" };
      case "solid":
      default:
        return { borderStyle: "solid" };
    }
  };

  const sizeStyles = getSizeStyles(size);
  const variantStyles = getVariantStyles(variant);

  return (
    <ChakraDivider
      orientation={orientation._value as "horizontal" | "vertical"}
      borderColor={resolvedColor}
      {...sizeStyles}
      {...variantStyles}
      {...resolvedCommonProps}
      {...remainingProps}
    />
  );
}

// Divider tokens
const dividerTokens = {
  size: {
    thin: createDividerSizeToken("thin"),
    medium: createDividerSizeToken("medium"),
    thick: createDividerSizeToken("thick"),
  } as const,

  variant: {
    solid: createDividerVariantToken("solid"),
    dashed: createDividerVariantToken("dashed"),
    dotted: createDividerVariantToken("dotted"),
  } as const,

  orientation: {
    horizontal: createDividerOrientationToken("horizontal"),
    vertical: createDividerOrientationToken("vertical"),
  } as const,
} as const;

// Public API - combines tokens and component
export const Divider = Object.assign(DividerComponent, dividerTokens);
